// Data export card: Blue Market owners and admins (client page → Access) and a client's admins (Account).
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post } from '../api';
import { ago, formatDateTime, useToast } from '../ui';

interface DataExport { id: string; scope: 'full' | 'client'; status: 'queued' | 'running' | 'ok' | 'failed' | 'expired'; requested_by: string;
  created_at: string; finished_at: string | null; expires_at: string | null; size_bytes: number | null; url: string | null; error: string }

const size = (n: number | null) => n == null ? '' : n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;

export function ExportsCard({ clientId, title, blurb }: { clientId: string; title: string; blurb: string }) {
  const toast = useToast();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['exports', clientId], queryFn: () => get<{ exports: DataExport[] }>(`/api/clients/${clientId}/exports`),
                              refetchInterval: (q) => q.state.data?.exports.some((e) => e.status === 'queued' || e.status === 'running') ? 3000 : false });
  const make = useMutation({ mutationFn: () => post(`/api/clients/${clientId}/exports`, {}), onSuccess: () => qc.invalidateQueries({ queryKey: ['exports', clientId] }),
                             onError: (e: Error) => toast(e.message, true) });
  const busy = data?.exports.some((e) => e.status === 'queued' || e.status === 'running');
  return (
    <section className="card pad stack" style={{ gap: 10 }}>
      <div className="row"><h2 className="grow">{title}</h2>
        <button className="btn sm" onClick={() => make.mutate()} disabled={busy || make.isPending}>{busy && <span className="spin" />}{busy ? 'Making the export…' : 'Make an export'}</button></div>
      <p className="muted small" style={{ margin: 0 }}>{blurb} It's a zip of spreadsheets (CSV) and files, ready in a minute or two; the link works for 7 days.</p>
      {(data?.exports ?? []).slice(0, 4).map((e) => (
        <div key={e.id} className="row small" style={{ gap: 8 }}>
          <span className="grow"><span title={formatDateTime(e.created_at)}>{ago(e.created_at)}</span> <span className="muted">· {e.requested_by}</span></span>
          {e.status === 'ok' && e.url && <><span className="muted tiny">{size(e.size_bytes)}</span><a className="btn sm ghost" href={e.url} download>Download</a></>}
          {(e.status === 'queued' || e.status === 'running') && <span className="muted tiny"><span className="spin" /> Making it…</span>}
          {e.status === 'expired' && <span className="muted tiny">Expired</span>}
          {e.status === 'failed' && <span className="bad-text tiny" title={e.error}>Failed</span>}
        </div>
      ))}
    </section>
  );
}
